"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

const ADJECTIVES: Record<string, string[]> = {
  a: ["angry", "ancient", "amber", "awkward", "agile", "arctic"],
  b: ["brave", "bright", "bumpy", "bold", "breezy", "bashful"],
  c: ["clever", "curious", "cozy", "crimson", "calm", "cheerful"],
  d: ["daring", "dusty", "dazzling", "dizzy", "dapper", "dreamy"],
  e: ["eager", "elegant", "enormous", "early", "electric", "emerald"],
  f: ["fancy", "fearless", "fluffy", "friendly", "frozen", "funny"],
  g: ["gentle", "giant", "glowing", "graceful", "grumpy", "golden"],
  h: ["happy", "hungry", "humble", "heroic", "hasty", "hollow"],
  i: ["icy", "itchy", "invisible", "iron", "imaginary", "impatient"],
  j: ["jolly", "jumpy", "jagged", "jazzy", "jovial", "juicy"],
  k: ["kind", "keen", "knobby", "knowing", "kingly", "kooky"],
  l: ["lazy", "loud", "lucky", "little", "lively", "lonely"],
  m: ["mighty", "merry", "muddy", "mellow", "modern", "mysterious"],
  n: ["noble", "nervous", "nimble", "noisy", "neat", "nifty"],
  o: ["odd", "orange", "old", "obedient", "optimistic", "oval"],
  p: ["purple", "polite", "proud", "playful", "prickly", "patient"],
  q: ["quiet", "quick", "quirky", "quaint", "quivering", "quizzical"],
  r: ["rapid", "rusty", "royal", "restless", "rosy", "rugged"],
  s: ["silly", "sleepy", "shiny", "sneaky", "sturdy", "silver"],
  t: ["tiny", "tidy", "ticklish", "tall", "tangled", "thoughtful"],
  u: ["unusual", "upbeat", "uneven", "urgent", "uncommon", "unlikely"],
  v: ["violet", "valiant", "velvet", "vivid", "vast", "vain"],
  w: ["witty", "wobbly", "wild", "warm", "weary", "wise"],
  x: ["xanthic", "xeric", "xenial", "xyloid", "x-shaped", "xerothermic"],
  y: ["young", "yellow", "yawning", "yearly", "yummy", "youthful"],
  z: ["zany", "zealous", "zesty", "zigzag", "zippy", "zonal"],
};

const NOUNS: Record<string, string[]> = {
  a: ["ants", "apples", "astronauts", "artists", "alligators", "acrobats"],
  b: ["bears", "bakers", "bees", "brothers", "badgers", "bandits"],
  c: ["cats", "camels", "captains", "chefs", "clowns", "cousins"],
  d: ["dogs", "dancers", "dragons", "doctors", "dolphins", "donkeys"],
  e: ["elephants", "eagles", "engineers", "elves", "emus", "explorers"],
  f: ["foxes", "farmers", "frogs", "firefighters", "falcons", "ferrets"],
  g: ["goats", "giants", "gardeners", "geese", "gorillas", "grandmothers"],
  h: ["horses", "hamsters", "herons", "hikers", "hedgehogs", "hawks"],
  i: ["iguanas", "inventors", "insects", "islanders", "ibises", "imps"],
  j: ["jaguars", "jesters", "judges", "jellyfish", "jackals", "jugglers"],
  k: ["kangaroos", "kittens", "knights", "kings", "koalas", "kayakers"],
  l: ["lions", "llamas", "lobsters", "librarians", "lizards", "leopards"],
  m: ["monkeys", "mice", "magicians", "mermaids", "moose", "musicians"],
  n: ["newts", "ninjas", "nurses", "nightingales", "narwhals", "neighbors"],
  o: ["otters", "owls", "octopuses", "orcas", "ostriches", "oxen"],
  p: ["pandas", "pirates", "penguins", "painters", "parrots", "poets"],
  q: ["quails", "queens", "quokkas", "quartets", "quiltmakers", "quarterbacks"],
  r: ["rabbits", "raccoons", "robots", "runners", "ravens", "rangers"],
  s: ["squirrels", "sailors", "snakes", "singers", "spiders", "sisters"],
  t: ["tigers", "turtles", "teachers", "toads", "tourists", "twins"],
  u: ["unicorns", "umpires", "uncles", "urchins", "ushers", "ukuleles"],
  v: [
    "vultures",
    "vikings",
    "violinists",
    "voles",
    "veterinarians",
    "voyagers",
  ],
  w: ["wolves", "walruses", "wizards", "wombats", "waiters", "weasels"],
  x: ["xylophones", "x-rays", "xeruses", "xebecs", "xenoliths", "xylographs"],
  y: ["yaks", "yodelers", "youngsters", "yetis", "yachts", "yearlings"],
  z: ["zebras", "zookeepers", "zombies", "zeppelins", "zealots", "zithers"],
};

const VERBS: Record<string, string[]> = {
  a: ["amble", "applaud", "argue", "arrive", "admire", "announce"],
  b: ["bounce", "bake", "bellow", "borrow", "build", "balance"],
  c: ["cheer", "climb", "chatter", "carry", "crawl", "celebrate"],
  d: ["dance", "dash", "daydream", "dig", "doodle", "deliver"],
  e: ["eat", "explore", "exercise", "examine", "escape", "entertain"],
  f: ["fly", "frolic", "fetch", "forget", "follow", "flip"],
  g: ["gallop", "giggle", "gather", "gaze", "grumble", "guard"],
  h: ["hop", "hum", "hurry", "hide", "howl", "hesitate"],
  i: ["imagine", "invent", "inspect", "invite", "itch", "improvise"],
  j: ["jump", "jog", "juggle", "jingle", "joke", "journey"],
  k: ["kick", "knit", "kneel", "kayak", "kiss", "keep"],
  l: ["leap", "laugh", "listen", "lounge", "lumber", "linger"],
  m: ["march", "mumble", "munch", "meander", "measure", "mingle"],
  n: ["nap", "nibble", "navigate", "nod", "notice", "negotiate"],
  o: ["observe", "obey", "order", "overreact", "orbit", "organize"],
  p: ["prance", "paint", "ponder", "practice", "pounce", "parade"],
  q: ["quack", "question", "quarrel", "quiver", "quote", "queue"],
  r: ["run", "race", "rest", "roam", "rejoice", "rummage"],
  s: ["skip", "sing", "snore", "sprint", "stumble", "sketch"],
  t: ["tiptoe", "tumble", "travel", "tease", "trot", "twirl"],
  u: ["unite", "unpack", "understand", "usher", "unwind", "unravel"],
  v: ["vanish", "volunteer", "vault", "visit", "vote", "venture"],
  w: ["wander", "whistle", "wave", "wobble", "whisper", "wrestle"],
  x: ["x-ray", "xerox", "xeriscape", "xylograph", "xerograph", "x-out"],
  y: ["yawn", "yodel", "yell", "yearn", "yield", "yank"],
  z: ["zoom", "zigzag", "zip", "zap", "zone", "zizz"],
};

const WORD_BANKS = [ADJECTIVES, NOUNS, VERBS];

const NON_LETTERS = /[^a-z]/g;

const MAX_LETTERS = 15;

const SENTENCES_PER_RUN = 3;

function extractLetters(input: string): string[] {
  return input
    .toLowerCase()
    .replace(NON_LETTERS, "")
    .slice(0, MAX_LETTERS)
    .split("");
}

function pickWord(letter: string, position: number): string {
  const bank = WORD_BANKS[position % WORD_BANKS.length];
  const words = bank[letter] ?? [letter.toUpperCase()];
  const word = words[Math.floor(Math.random() * words.length)];
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function buildSentence(letters: string[]): string {
  return `${letters.map((letter, position) => pickWord(letter, position)).join(" ")}.`;
}

function buildSentences(letters: string[]): string[] {
  const sentences = new Set<string>();
  let attempts = 0;
  while (sentences.size < SENTENCES_PER_RUN && attempts < 30) {
    sentences.add(buildSentence(letters));
    attempts += 1;
  }
  return [...sentences];
}

/** English strings. A translated content file overrides these through `ui`. */
export const MNEMONIC_GENERATOR_STRINGS = {
  lettersOrWordToRemember: "Letters or word to remember",
  eGPemdas: "e.g. PEMDAS",
  letter: "one: letter|other: letters",
  max: " (max",
  spacesAndPunctuationAreIgnored: " — spaces and punctuation are ignored)",
  tryAnExample: "Try an example",
  regenerate: "Regenerate",
  generateMnemonics: "Generate Mnemonics",
  acrosticSentencesFor: "Acrostic sentences for",
  copied: "Copied!",
  copy: "Copy",
  presetPemdasHint: "Order of operations",
  presetHomesHint: "The Great Lakes",
  presetRoygbivHint: "Colors of the rainbow",
} as const;

export default function MnemonicGeneratorWidget() {
  const [input, setInput] = useState("");
  const [sentences, setSentences] = useState<string[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const letters = extractLetters(input);

  function generateFrom(value: string) {
    const extracted = extractLetters(value);
    if (extracted.length === 0) {
      return;
    }
    setSentences(buildSentences(extracted));
    setCopiedIndex(null);
  }

  function handleGenerate() {
    generateFrom(input);
  }

  function handlePreset(presetLetters: string) {
    setInput(presetLetters);
    generateFrom(presetLetters);
  }

  function handleCopy(sentence: string, index: number) {
    writeToClipboard(sentence).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  const locale = useToolLocale();
  const strings = useToolUi(MNEMONIC_GENERATOR_STRINGS);
  const presets: { label: string; letters: string; hint: string }[] = [
    { label: "PEMDAS", letters: "PEMDAS", hint: strings.presetPemdasHint },
    { label: "HOMES", letters: "HOMES", hint: strings.presetHomesHint },
    { label: "ROYGBIV", letters: "ROYGBIV", hint: strings.presetRoygbivHint },
  ];

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="mnemonic-input"
        >
          {strings.lettersOrWordToRemember}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="mnemonic-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.eGPemdas}
          type="text"
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {letters.length}
          {tPlural(strings.letter, locale, letters.length)}
          {strings.max} {MAX_LETTERS}
          {strings.spacesAndPunctuationAreIgnored}
        </p>
      </div>
      {/* Presets */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.tryAnExample}
        </p>
        <div className="flex flex-wrap gap-3">
          {presets.map((preset) => (
            <button
              className="rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors hover:bg-muted"
              key={preset.label}
              onClick={() => handlePreset(preset.letters)}
              type="button"
            >
              <span className="font-medium text-foreground">
                {preset.label}
              </span>{" "}
              <span className="text-muted-foreground">— {preset.hint}</span>
            </button>
          ))}
        </div>
      </section>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={letters.length === 0}
          onClick={handleGenerate}
          type="button"
        >
          {sentences.length > 0
            ? strings.regenerate
            : strings.generateMnemonics}
        </button>
      </div>
      {/* Results */}
      {sentences.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.acrosticSentencesFor}{" "}
            <span className="font-mono uppercase">{letters.join("")}</span>
          </h2>
          <ul className="space-y-3">
            {sentences.map((sentence, index) => (
              <li
                className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-5"
                key={sentence}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  {sentence}
                </p>
                <button
                  className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                  onClick={() => handleCopy(sentence, index)}
                  type="button"
                >
                  {copiedIndex === index ? strings.copied : strings.copy}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
